import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Alert, 
  Card, 
  CardContent,
  CircularProgress,
  Grid,
  Chip
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import axios from 'axios';

function ApiTester() {
  const [testResults, setTestResults] = useState({});
  const [loading, setLoading] = useState(false);

  const testEndpoints = [
    { name: 'Bulk Data', url: 'https://servermain.sisrevolution.com/api/schools/getbulkdata', method: 'POST' },
    { name: 'Students Data', url: 'https://servermain.sisrevolution.com/api/students', method: 'GET' },
    { name: 'Teachers Data', url: 'https://servermain.sisrevolution.com/api/teachers', method: 'GET' },
    { name: 'Expenses Data', url: 'https://servermain.sisrevolution.com/api/expenditure', method: 'GET' },
    { name: 'Sales Data', url: 'https://servermain.sisrevolution.com/api/sales', method: 'GET' },
    { name: 'Assets Data', url: 'https://servermain.sisrevolution.com/api/assets', method: 'GET' },
    { name: 'Classes Data', url: 'https://servermain.sisrevolution.com/api/class', method: 'GET' },
    { name: 'Schools Data', url: 'https://servermain.sisrevolution.com/api/schools', method: 'GET' }
  ];

  const testEndpoint = async (endpoint) => {
    try {
      const config = {
        method: endpoint.method.toLowerCase(),
        url: endpoint.url,
        timeout: 5000,
        headers: {
          'Content-Type': 'application/json'
        }
      };

      if (endpoint.method === 'POST') {
        config.data = { schoolID: 'test' };
      }

      const response = await axios(config);
      
      const dataLength = Array.isArray(response.data) ? response.data.length : 
                        (response.data?.data ? response.data.data.length : 
                         (response.data?.students ? response.data.students.length : 'N/A'));
      
      const isEmpty = dataLength === 0 || dataLength === 'N/A';
      
      return {
        status: isEmpty ? 'warning' : 'success',
        statusCode: response.status,
        dataLength: dataLength,
        message: isEmpty ? 'API responded but no data found' : 'API responded successfully',
        isEmpty: isEmpty
      };
    } catch (error) {
      return {
        status: 'error',
        statusCode: error.response?.status || 'No Response',
        message: error.message || 'Unknown error',
        error: error.code || 'NETWORK_ERROR'
      };
    }
  };

  const runAllTests = async () => {
    setLoading(true);
    const results = {};

    for (const endpoint of testEndpoints) {
      console.log(`Testing ${endpoint.name}...`);
      const result = await testEndpoint(endpoint);
      results[endpoint.name] = result;
      
      // Small delay between requests
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    setTestResults(results);
    setLoading(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon color="warning" />;
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'success':
        return <Chip label="Success" color="success" size="small" />;
      case 'warning':
        return <Chip label="No Data" color="warning" size="small" />;
      case 'error':
        return <Chip label="Error" color="error" size="small" />;
      default:
        return <Chip label="Unknown" color="default" size="small" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        API Tester
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3 }}>
        Test backend API endpoints to ensure data is available for the accounting system.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          onClick={runAllTests}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Testing APIs...' : 'Test All Endpoints'}
        </Button>
      </Box>

      {Object.keys(testResults).length > 0 && (
        <Grid container spacing={2}>
          {Object.entries(testResults).map(([name, result]) => (
            <Grid item xs={12} md={6} key={name}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="h6">
                      {name}
                    </Typography>
                    {getStatusIcon(result.status)}
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {getStatusChip(result.status)}
                    <Typography variant="body2" color="text.secondary">
                      Status: {result.statusCode}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {result.message}
                  </Typography>
                  
                  {result.dataLength && (
                    <Typography variant="body2" color="text.secondary">
                      Data Length: {result.dataLength}
                    </Typography>
                  )}
                  
                  {result.error && (
                    <Typography variant="body2" color="error">
                      Error: {result.error}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Expected Results
          </Typography>
          <Typography variant="body2" component="div">
            <ul>
              <li><strong>Backend Health:</strong> Should return 200 OK</li>
              <li><strong>Data Endpoints:</strong> Should return arrays of data</li>
              <li><strong>Bulk Data:</strong> Should return comprehensive data object</li>
              <li><strong>All endpoints:</strong> Should respond within 5 seconds</li>
            </ul>
          </Typography>
        </CardContent>
      </Card>

      {Object.keys(testResults).length > 0 && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Note:</strong> If any endpoints are failing, check that the backend server 
            is running on port 3008 and that the database is accessible.
          </Typography>
        </Alert>
      )}
    </Box>
  );
}

export default ApiTester;
